import { RefObject, useCallback, useLayoutEffect, useState } from 'react'
import { throttle } from 'underscore'

type HorizontalOverflow = { hasLeftOverflow: boolean; hasRightOverflow: boolean; visibleWidth: number }

const NO_OVERFLOW: HorizontalOverflow = { hasLeftOverflow: false, hasRightOverflow: false, visibleWidth: 0 }

/** Which way a scroller still has content, so an edge only marks itself while it hides something. */
export const useHorizontalOverflow = <T extends HTMLElement>(ref: RefObject<T>): HorizontalOverflow => {
    const [overflow, setOverflow] = useState(NO_OVERFLOW)

    const check = useCallback(() => {
        const { current } = ref
        if (!current) return

        const { scrollLeft, scrollWidth, clientWidth } = current
        const hasLeftOverflow = scrollLeft > 0
        const hasRightOverflow = Math.ceil(scrollLeft + clientWidth) < scrollWidth

        setOverflow((previous) =>
            previous.hasLeftOverflow === hasLeftOverflow &&
            previous.hasRightOverflow === hasRightOverflow &&
            previous.visibleWidth === clientWidth
                ? previous
                : { hasLeftOverflow, hasRightOverflow, visibleWidth: clientWidth }
        )
    }, [ref])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledCheck = useCallback(throttle(check, 100), [check])

    // No dependency list: a column coming or going changes the answer, and the comparison above
    // keeps an unchanged answer from rendering again.
    useLayoutEffect(() => {
        const element = ref.current
        check()

        element?.addEventListener('scroll', throttledCheck)
        window.addEventListener('resize', throttledCheck)

        return () => {
            element?.removeEventListener('scroll', throttledCheck)
            window.removeEventListener('resize', throttledCheck)
        }
    })

    return overflow
}

export default useHorizontalOverflow
